import { useCallback, useEffect, useRef, useState } from 'react';
import { runnerApi, RunnerApiError, type Me, type MyApplication } from './runnerApiClient';
import { adoptServerLanguage, setLanguageSaver } from '../i18n';

export interface RunnerState {
  me: Me | null;
  application: MyApplication | null;
  /** True only for the FIRST load. A later reload() does not set this, so the
   *  page stays mounted and callers keep their local state (a success message,
   *  a scroll position, an open panel). */
  loading: boolean;
  /** True while a post-mount refresh is in flight, for subtle in-place hints. */
  refreshing: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

/**
 * The signed-in Runner's identity + (for applicants) their application. One
 * fetch per mount; `reload()` after a mutation. A 404 on the application is
 * normal for a Runner-pool account that was created outside the intake (e.g.
 * a test identity) — it simply means "no application on file".
 */
export function useRunner(): RunnerState {
  const [me, setMe] = useState<Me | null>(null);
  const [application, setApplication] = useState<MyApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasLoaded = useRef(false);

  const reload = useCallback(async () => {
    if (hasLoaded.current) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const m = await runnerApi.me();
      setMe(m);
      setLanguageSaver(m.recovery ? null : runnerApi.setLanguage);
      void adoptServerLanguage(m);
      if (!m.recovery && (m.lifecycle === 'applicant' || m.handbookAckRequired)) {
        try {
          const a = await runnerApi.myApplication();
          setApplication(a.application);
        } catch (err) {
          if (err instanceof RunnerApiError && err.status === 404) setApplication(null);
          else throw err;
        }
      } else {
        setApplication(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your Runner profile');
    } finally {
      hasLoaded.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { me, application, loading, refreshing, error, reload };
}
